export const LAST_UPDATED = 'August 13, 2026';
export const CONTACT_EMAIL = 'harrym@eachbase.com';
export const WEBSITE = 'https://caretechtrans.com';

export const PRIVACY_SECTIONS = [
  {
    title: '1. Company and Contact Information',
    content: [
      'The CareTech Services are operated by CareTech.',
      'For privacy-related questions, concerns, or requests regarding personal information, please contact:',
      {
        contact: [
          { label: 'Email:', email: CONTACT_EMAIL },
          { label: 'Website:', link: WEBSITE },
        ],
      },
    ],
  },
  {
    title: '2. Account and Registration',
    content: [
      'The CareTech Services may be accessed by authorized users whose accounts and access credentials are created and managed by CareTech or by an organization responsible for providing access to the Services.',
      'Users cannot create a CareTech account through the Mobile App.',
      'Depending on the Services provided to you, account access may be provided through the CareTech web application or other administrative processes outside the Mobile App.',
      {
        text: 'Users should contact their organization or CareTech at ',
        email: CONTACT_EMAIL,
        suffix: ' regarding account access, credentials, or account-related questions.',
      },
    ],
  },
  {
    title: '3. Information We Collect',
    content: [
      {
        sub: '3.1 Account and Authentication Information',
        content: [
          'We may process information necessary to authenticate and authorize an existing user, including:',
          {
            list: [
              'Name',
              'Email address',
              'Phone number',
              'Username or employee identification information',
              'Authentication-related information',
              'Account status',
              'User role and permissions',
              'Information necessary to authenticate and authorize access to the Services',
            ],
          },
          'The information may be provided or managed by CareTech or by an organization that provides users with access to the Services.',
        ],
      },
      {
        sub: '3.2 Employee and Professional Information',
        content: [
          "We may process information related to a user's professional or work role, including:",
          {
            list: [
              'Employee identification information',
              'Driver information',
              'Assigned role or permissions',
              'Work-related information',
              'Information necessary to assign and manage transportation services',
            ],
          },
        ],
      },
      {
        sub: '3.3 Trip and Transportation Information',
        content: [
          'The Services may process information associated with transportation services, scheduled trips, fleet operations, routes, and deliveries, including:',
          {
            list: [
              'Trip details',
              'Pickup and drop-off information',
              'Trip status',
              'Mileage',
              'Transportation-related notes',
              'Route information',
              'Delivery information',
              'Date and time information',
              'Information entered by authorized users',
              'Other information necessary to provide and manage transportation services',
            ],
          },
        ],
      },
      {
        sub: '3.4 Website and Web Application Information',
        content: [
          'When you use the CareTech website or web application, we may collect technical and usage information such as:',
          {
            list: [
              'IP address',
              'Browser type and version',
              'Operating system',
              'Device type',
              'Application or browser version',
              'Network information',
              'Access times',
              'Pages or application areas accessed',
              'Technical and diagnostic information',
              'Error and log information',
            ],
          },
          'This information may be used to operate, secure, troubleshoot, and improve the Services.',
        ],
      },
      {
        sub: '3.5 Mobile Device and Technical Information',
        content: [
          'When you use the CareTech Mobile App, we may process technical information associated with the device, including:',
          {
            list: [
              'Device manufacturer and model',
              'Device type',
              'Operating system and operating system version',
              'Application version',
              'Device or application identifiers',
              'Network information',
              'Technical and diagnostic information',
              'Application performance information',
              'Error and log information',
            ],
          },
          'We do not use device location information through the Mobile App.',
        ],
      },
      {
        sub: '3.6 Push Notifications',
        content: [
          'The Mobile App uses push notification functionality to provide service-related notifications.',
          'When push notifications are enabled, the Mobile App may process information required to deliver notifications to a device, such as a Firebase Cloud Messaging registration token or other notification-related identifier.',
          'Push notifications may be used for:',
          {
            list: [
              'Trip updates',
              'Work-related notifications',
              'Service notifications',
              'Security notifications',
              'Other communications related to the operation of the Services',
            ],
          },
          'Users may control notification permissions through their device settings.',
        ],
      },
      {
        sub: '3.7 Information Stored Locally on the Mobile Device',
        content: [
          "The Mobile App may store certain information locally on the user's device to maintain application functionality and user preferences. This may include:",
          {
            list: [
              'Application state',
              'User preferences',
              'Cached application information',
              'Authentication or session-related information',
              'Information necessary for the Mobile App to function correctly',
            ],
          },
        ],
      },
      {
        sub: '3.8 Information You Provide',
        content: [
          'Users may voluntarily provide information through the Services, including information entered through the web application or Mobile App. This may include information related to:',
          {
            list: [
              'Transportation services',
              'Trip management',
              'Fleet operations',
              'Mileage',
              'Work activities',
              'Deliveries',
              'Support requests',
              'Other information necessary to use the Services',
            ],
          },
        ],
      },
    ],
  },
  {
    title: '4. How We Use Information',
    content: [
      'We use personal information for legitimate business and service-related purposes, including:',
      {
        list: [
          'Providing and operating the Services',
          'Authenticating authorized users',
          'Authorizing access to Services and features',
          'Managing transportation services',
          'Managing fleets, routes, trips, and deliveries',
          'Assigning and managing trips',
          'Recording mileage and trip information',
          'Communicating with authorized users',
          'Sending service-related notifications',
          'Providing customer and technical support',
          'Maintaining and improving the Services',
          'Troubleshooting technical problems',
          'Maintaining the security and integrity of our systems',
          'Detecting, preventing, and investigating fraud, misuse, and security incidents',
          'Complying with applicable legal and regulatory requirements',
          'Establishing, exercising, or defending legal claims',
        ],
      },
      'We do not sell personal information for advertising purposes.',
    ],
  },
  {
    title: '5. How We Share Information',
    content: [
      'We may share or provide access to personal information when necessary to operate the Services, provide transportation-related services, comply with legal obligations, or protect our rights.',
      {
        sub: '5.1 CareTech Personnel and Authorized Users',
        content: [
          'Information may be accessible to authorized CareTech personnel and authorized users when necessary to perform their assigned responsibilities and provide the Services. Access is controlled according to applicable roles and permissions.',
        ],
      },
      {
        sub: '5.2 Service Providers',
        content: [
          'We may use trusted third-party service providers to support the operation and security of the Services. These providers may process information on our behalf for purposes such as:',
          {
            list: [
              'Cloud hosting and storage',
              'Authentication',
              'Push notifications',
              'Application infrastructure',
              'Security',
              'Technical services',
              'Error reporting and diagnostics',
              'Technical support',
            ],
          },
          'Service providers are expected to process information only for authorized purposes and to maintain appropriate security safeguards.',
        ],
      },
      {
        sub: '5.3 Firebase Cloud Messaging',
        content: [
          'The Mobile App uses Firebase Cloud Messaging provided by Google to deliver push notifications. Firebase may process technical and service-related information necessary to provide the messaging service.',
        ],
      },
      {
        sub: '5.4 Legal and Regulatory Requirements',
        content: [
          'We may disclose information when reasonably necessary to:',
          {
            list: [
              'Comply with applicable laws or regulations',
              'Respond to lawful requests from government authorities',
              'Protect the rights, property, or safety of CareTech, our users, or others',
              'Investigate fraud, abuse, or security incidents',
              'Establish, exercise, or defend legal claims',
            ],
          },
        ],
      },
    ],
  },
  {
    title: '6. Data Security',
    content: [
      'We take reasonable administrative, technical, and organizational measures to protect personal information against unauthorized access, disclosure, alteration, loss, or destruction. Security measures may include:',
      {
        list: [
          'Encryption of data transmitted over networks',
          'Access controls and authentication',
          'Role-based access restrictions',
          'Secure server infrastructure',
          'Monitoring and logging',
          'Security updates',
          'Protection of authentication information',
          'Limiting access to personal information to authorized personnel who require it for legitimate business purposes',
        ],
      },
      'No method of transmission or electronic storage is completely secure. Therefore, while we take reasonable measures to protect information, we cannot guarantee absolute security.',
    ],
  },
  {
    title: '7. Data Retention',
    content: [
      'We retain personal information only for as long as reasonably necessary to:',
      {
        list: [
          'Provide and operate the Services',
          'Maintain user access',
          'Maintain business and transportation records',
          'Fulfill contractual obligations',
          'Comply with applicable legal, regulatory, accounting, or reporting requirements',
          'Resolve disputes',
          'Prevent fraud and abuse',
          'Establish, exercise, or defend legal claims',
          'Maintain security and audit records',
        ],
      },
      'Different categories of information may be retained for different periods depending on their purpose and applicable legal or regulatory requirements.',
      'When personal information is no longer required for these purposes, we will delete, anonymize, or securely dispose of it in accordance with applicable data retention procedures.',
      'Certain information may be retained when retention is required or permitted by law, regulatory requirements, legitimate security purposes, fraud prevention, dispute resolution, accounting requirements, or legal obligations.',
    ],
  },
  {
    title: '8. Data Deletion and Data Deletion Requests',
    content: [
      'The CareTech Mobile App does not provide a self-service account creation or account deletion function.',
      'Users who want to request deletion of personal information processed by CareTech may contact us at:',
      { contact: [{ label: 'Email:', email: CONTACT_EMAIL }] },
      'Please provide sufficient information for us to identify the relevant account or information and process the request securely.',
      'We will review and process valid deletion requests within a reasonable period and in accordance with applicable law.',
      'When deletion is appropriate, we will delete or anonymize the applicable personal information unless we are required or permitted to retain certain information for legitimate purposes, including legal, regulatory, security, fraud-prevention, accounting, or dispute-resolution purposes.',
      'Where information must be retained for one of these reasons, it will be retained only for the period required or permitted for that purpose and will not be used for unrelated purposes.',
    ],
  },
  {
    title: '9. User Privacy Rights',
    content: [
      "Depending on the user's location and applicable law, users may have rights regarding their personal information, including:",
      {
        list: [
          'Requesting access to personal information',
          'Requesting correction of inaccurate information',
          'Requesting deletion of personal information',
          'Requesting restriction of certain processing',
          'Objecting to certain processing',
          'Requesting a copy of certain information',
          'Withdrawing consent where processing is based on consent',
        ],
      },
      { text: 'To exercise applicable privacy rights, contact: ', email: CONTACT_EMAIL },
      'We may need to verify the identity and authority of a person making a request before processing certain requests.',
    ],
  },
  {
    title: "10. Children's Privacy",
    content: [
      'The Services are intended for authorized personnel and other authorized users and are not directed toward children.',
      'We do not knowingly collect personal information from children where such collection is prohibited by applicable law.',
    ],
  },
  {
    title: '11. Third-Party Services',
    content: [
      'The Services may use third-party software and services to provide certain functionality.',
      {
        sub: 'Google Firebase / Firebase Cloud Messaging',
        content: ['Used by the Mobile App for push notifications and related application services.'],
      },
      {
        sub: 'Other service providers',
        content: [
          'CareTech may use third-party providers for hosting, infrastructure, security, authentication, technical support, and other services necessary to operate the platform.',
        ],
      },
      'Third-party providers may process information according to their applicable terms and privacy policies.',
    ],
  },
  {
    title: '12. Telephone Functionality',
    content: [
      "If the Mobile App provides functionality that allows an authorized user to initiate a telephone call, the telephone call is handled by the device's operating system and applicable telecommunications provider.",
      'CareTech does not use telephone functionality for advertising purposes.',
    ],
  },
  {
    title: '13. Cookies and Website Technologies',
    content: [
      'The CareTech website and web application may use cookies or similar technologies for necessary website functionality, security, analytics, session management, or other legitimate purposes.',
      'Cookies and similar technologies may be used to:',
      {
        list: [
          'Maintain sessions',
          'Secure the website and web application',
          'Remember user preferences',
          'Understand website usage',
          'Diagnose technical issues',
          'Improve the Services',
        ],
      },
      'Users may be able to control cookies through their web browser settings.',
    ],
  },
  {
    title: '14. Changes to This Privacy Policy',
    content: [
      'We may update this Privacy Policy from time to time to reflect changes in our Services, technology, legal requirements, or privacy practices.',
      'When we make changes, we will update the "Last Updated" date at the top of this Privacy Policy.',
      'If required by applicable law, we will provide additional notice regarding material changes.',
    ],
  },
  {
    title: '15. Contact Us',
    content: [
      'If you have questions, concerns, or requests regarding this Privacy Policy or our privacy practices, please contact:',
      {
        contact: [
          { label: 'CareTech' },
          { label: 'Email:', email: CONTACT_EMAIL },
          { label: 'Website:', link: WEBSITE },
        ],
      },
    ],
  },
];
